import { useEffect, useRef, useState } from "react"
import { Box, IconButton, Slide, Stack, Typography } from "@mui/material"
import CloseIcon from "@mui/icons-material/CloseRounded"
import ChatIcon from "@mui/icons-material/ChatBubbleOutlineRounded"
import TaskIcon from "@mui/icons-material/AssignmentOutlined"
import ProjectIcon from "@mui/icons-material/FolderOutlined"
import ClientIcon from "@mui/icons-material/ApartmentRounded"
import AlertIcon from "@mui/icons-material/PriorityHighRounded"
import BellIcon from "@mui/icons-material/NotificationsNoneRounded"

import { useNotifications } from "../context/NotificationContext"
import { MOTION, transition } from "../theme"

/**
 * In-app popups for someone who is looking at the app right now.
 *
 * ── Why the top, not the bottom ─────────────────────────────────────────────
 *
 * These used to sit in the bottom-right corner, which is where the timer bar,
 * the composer and every confirmation snackbar already are. A message landing
 * there arrives underneath the thing somebody is typing into, and on a chat
 * screen it is directly over the send button.
 *
 * At the top right it lands under the bell it is a preview of, which is also
 * where it goes when it is dismissed — the toast and its resting place are in
 * the same part of the screen, so the connection does not have to be explained.
 *
 * ── Why not a MUI Snackbar ──────────────────────────────────────────────────
 *
 * A Snackbar shows one message at a time and queues the rest, so during a busy
 * minute the third notification appears fifteen seconds after it happened.
 * These stack, are capped at three by the context, and each dismisses on its
 * own timer.
 */
const AUTO_DISMISS_MS = 7000

const ICONS = {
  chat_room: ChatIcon,
  task: TaskIcon,
  project: ProjectIcon,
  organization: ClientIcon,
}

/**
 * The accent, from the palette rather than from MUI's severity colours.
 *
 * `info` blue and `warning` orange are the two colours the rest of this app
 * never uses for something ordinary, so a filled Alert in either read as an
 * error had happened. A message is brand green, because a message is the normal
 * case; only a high-priority notification gets to be orange, and it earns it.
 */
function accentFor(toast, theme) {
  if (toast.priority === "high") return theme.palette.warning.main
  if (toast.ref_type === "chat_room") return theme.palette.primary.dark
  return theme.palette.info.main
}

export default function NotificationToasts() {
  const { toasts, dismissToast, openNotification } = useNotifications()

  if (toasts.length === 0) return null

  return (
    <Box
      sx={{
        position: "fixed",
        // Clear of the app bar, which is 56px tall on a phone and 64 above it.
        top: { xs: 64, sm: 72 },
        right: { xs: 12, sm: 24 },
        left: { xs: 12, sm: "auto" },
        zIndex: (theme) => theme.zIndex.snackbar,
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        width: { xs: "auto", sm: 380 },
        // Not on the top layer for pointer events: an empty stack must not sit
        // invisibly over the page and swallow clicks.
        pointerEvents: "none",
      }}
    >
      {/* Newest first. Appending downwards would mean the one that just arrived
          is the one furthest from the bell, and the stack would shuffle upward
          under the reader's eye every time an older one expired. */}
      {[...toasts].reverse().map((toast) => (
        <Toast key={toast.key} toast={toast} onDismiss={dismissToast} onOpen={openNotification} />
      ))}
    </Box>
  )
}

function Toast({ toast, onDismiss, onOpen }) {
  const [held, setHeld] = useState(false)
  const Icon = ICONS[toast.ref_type] || (toast.priority === "high" ? AlertIcon : BellIcon)

  /*
   * One timer per toast, in an effect rather than a ref callback.
   *
   * The previous version started the timer inside `ref={(node) => …}`, which
   * React calls again on every re-render that replaces the node — so a toast
   * could end up with two timers and no way to clear either, and the cleanup
   * never ran at all.
   */
  const dismiss = useRef(onDismiss)
  dismiss.current = onDismiss
  useEffect(() => {
    if (held) return undefined
    const timer = setTimeout(() => dismiss.current(toast.key), AUTO_DISMISS_MS)
    return () => clearTimeout(timer)
  }, [toast.key, held])

  return (
    <Slide direction="down" in mountOnEnter unmountOnExit timeout={{ enter: MOTION.slow, exit: MOTION.fast }}>
      <Box
        role="alert"
        // Hovering holds it. A notification that disappears while it is being
        // read is worse than one that never arrived, because the reader knows
        // they missed something and cannot say what.
        onMouseEnter={() => setHeld(true)}
        onMouseLeave={() => setHeld(false)}
        onFocus={() => setHeld(true)}
        onBlur={() => setHeld(false)}
        onClick={() => {
          onOpen({ ...toast, content_id: toast.id })
          onDismiss(toast.key)
        }}
        // The card is the open target, so it has to be one by keyboard too —
        // otherwise the only thing a keyboard reaches on a notification is the
        // button that throws it away.
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key !== "Enter" && event.key !== " ") return
          event.preventDefault()
          onOpen({ ...toast, content_id: toast.id })
          onDismiss(toast.key)
        }}
        sx={(theme) => ({
          pointerEvents: "auto",
          cursor: "pointer",
          position: "relative",
          overflow: "hidden",
          borderRadius: 2.5,
          bgcolor: "background.paper",
          border: 1,
          borderColor: "divider",
          // The same two-shadow rule as a dialog: the near one seats it on the
          // page, the far one is the light falling off it.
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 1px 3px rgba(0,0,0,0.5), 0 14px 32px -10px rgba(0,0,0,0.6)"
              : "0 1px 3px rgba(16,35,10,0.08), 0 14px 32px -10px rgba(16,35,10,0.24)",
          transition: transition(["transform", "box-shadow", "border-color"], MOTION.fast),
          "&:focus-visible": {
            outline: (t) => `2px solid ${t.palette.primary.dark}`,
            outlineOffset: 2,
          },
          "&:hover": {
            transform: "translateY(-1px)",
            borderColor: accentFor(toast, theme),
          },
          // The accent rail. A whole filled panel in the accent colour is what
          // this replaced — at 380px wide it read as an error banner whatever
          // the colour was.
          "&::before": {
            content: '""',
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 4,
            bgcolor: accentFor(toast, theme),
          },
        })}
      >
        <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ p: 1.5, pl: 2 }}>
          <Box
            // The kind, named. A production build strips MUI's own icon test
            // ids, so without this there is nothing on the page that says which
            // icon was chosen — and the icon is the part somebody reads before
            // the words.
            data-kind={toast.ref_type || "other"}
            sx={(theme) => ({
              flexShrink: 0,
              width: 32,
              height: 32,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              // A wash of the accent rather than the accent itself, so the icon
              // is legible on it in both themes without a second colour choice.
              bgcolor: `${accentFor(toast, theme)}1f`,
              color: accentFor(toast, theme),
            })}
          >
            <Icon sx={{ fontSize: 18 }} />
          </Box>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" fontWeight={650} sx={{ lineHeight: 1.35 }}>
              {toast.title}
            </Typography>
            {toast.body ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  mt: 0.25,
                }}
              >
                {toast.body}
              </Typography>
            ) : null}
          </Box>

          <IconButton
            size="small"
            aria-label="Dismiss this notification"
            onClick={(event) => {
              // Without this the dismiss button also opens the thing it is
              // dismissing, because the whole card is the open target.
              event.stopPropagation()
              onDismiss(toast.key)
            }}
            sx={{ mt: -0.25, mr: -0.5 }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Stack>

        {/*
          How long is left, as a bar that drains.
          A toast that vanishes with no warning reads as a glitch; one with a
          visible clock reads as a decision. It stops while the pointer is on
          the card, which is also the signal that hovering held it.

          Driven by a CSS animation rather than React state so it does not
          re-render the card sixty times a second — and so `prefers-reduced-
          motion` in index.css switches it off along with everything else.
        */}
        <Box
          sx={(theme) => ({
            height: 2,
            bgcolor: accentFor(toast, theme),
            opacity: 0.55,
            transformOrigin: "left",
            animation: `tw-toast-drain ${AUTO_DISMISS_MS}ms linear forwards`,
            animationPlayState: held ? "paused" : "running",
            "@keyframes tw-toast-drain": {
              from: { transform: "scaleX(1)" },
              to: { transform: "scaleX(0)" },
            },
          })}
        />
      </Box>
    </Slide>
  )
}
